/**
 * Chat Preferences — localStorage-backed store
 *
 * Persists user's chat toolbar selections across page refreshes.
 * No React context or external dependency — just a simple hook + pure functions.
 */

import { useState, useCallback } from 'react';

export interface ChatPreferences {
  /** Model ID in format 'provider:tier', e.g. 'openai:quality' */
  modelId: string;
  /** Enable web search augmentation */
  webSearch: boolean;
  /** Enable extended thinking / step-by-step reasoning */
  extendedThinking: boolean;
}

const STORAGE_KEY = 'hr_chat_preferences';

const DEFAULTS: ChatPreferences = {
  modelId: 'openai:quality',
  webSearch: false,
  extendedThinking: false,
};

/** Read persisted preferences (pure function, no hooks) */
export function getChatPreferences(): ChatPreferences {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULTS };
  }
}

/** Write preferences to localStorage (pure function, no hooks) */
export function setChatPreferences(partial: Partial<ChatPreferences>): ChatPreferences {
  const current = getChatPreferences();
  const next = { ...current, ...partial };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* quota exceeded — ignore */
  }
  return next;
}

/** Derive modelTier from a modelId like 'openai:quality' -> 'quality' */
export function getModelTier(modelId: string): 'fast' | 'quality' {
  const tier = modelId.split(':')[1];
  return tier === 'fast' ? 'fast' : 'quality';
}

/**
 * React hook — returns [preferences, updatePreferences]
 * Re-renders on update. Persists to localStorage automatically.
 */
export function useChatPreferences(): [ChatPreferences, (p: Partial<ChatPreferences>) => void] {
  const [prefs, setPrefs] = useState<ChatPreferences>(getChatPreferences);

  const update = useCallback((partial: Partial<ChatPreferences>) => {
    setPrefs(prev => {
      const next = { ...prev, ...partial };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  return [prefs, update];
}
